import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';
import aiDevelopmentSnapshot from '../data/aiDevelopment';
import { useLanguage } from '../contexts/LanguageContext';
import AIDevelopmentHeader from '../components/ai-development/AIDevelopmentHeader';
import ModelReleaseFrontierSection from '../components/ai-development/ModelReleaseFrontierSection';
import ModuleNavigation from '../components/ai-development/ModuleNavigation';
import CapabilitySection from '../components/ai-development/CapabilitySection';
import CostEfficiencySection from '../components/ai-development/CostEfficiencySection';
import UsageMonetizationSection from '../components/ai-development/UsageMonetizationSection';
import ComputeMarketSection from '../components/ai-development/ComputeMarketSection';
import EconomicDiffusionSection from '../components/ai-development/EconomicDiffusionSection';
import '../components/ai-development/aiDevelopment.css';
import { PRODUCT_NAME } from '../config/brand';

const AIFuturePage = () => {
    const { language } = useLanguage();
    const isZh = language === 'zh';
    const [dashboard, setDashboard] = useState(null);
    const [range, setRange] = useState('All');
    const [activeId, setActiveId] = useState('capability');
    const [loadError, setLoadError] = useState(false);

    const loadDashboard = useCallback(async () => {
        setLoadError(false);
        try {
            const response = await apiService.getAIFutureDashboard();
            setDashboard(response?.data || response || aiDevelopmentSnapshot);
        } catch (error) {
            console.error('Failed to load AI development monitor:', error);
            setDashboard(aiDevelopmentSnapshot);
            setLoadError(true);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = isZh ? `AI 发展监测 | ${PRODUCT_NAME}` : `AI Development Monitor | ${PRODUCT_NAME}`;
        return () => { document.title = previousTitle; };
    }, [isZh]);

    useEffect(() => {
        if (!dashboard || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver((entries) => {
            const visible = entries
                .filter((entry) => entry.isIntersecting)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (visible?.target?.id) setActiveId(visible.target.id.replaceAll('-', '_'));
        }, { rootMargin: '-22% 0px -62% 0px', threshold: [0, 0.05, 0.25, 0.5] });
        document.querySelectorAll('[data-testid="ai-development-module"]').forEach((node) => observer.observe(node));
        return () => observer.disconnect();
    }, [dashboard]);

    if (!dashboard) {
        return <div className="ai-dev-loading" role="status">{isZh ? '正在加载可追溯数据…' : 'Loading traceable data…'}</div>;
    }

    return (
        <div className="ai-dev-page">
            <div className="ai-dev-shell">
                {loadError && (
                    <div className="ai-dev-error" role="alert">
                        <span>{isZh ? '远端读取失败，当前展示仓库内的同日数据快照。' : 'Remote load failed; showing the same-day repository snapshot.'}</span>
                        <button type="button" onClick={loadDashboard}>{isZh ? '重试' : 'Retry'}</button>
                    </div>
                )}
                <AIDevelopmentHeader
                    asOf={dashboard.asOf}
                    range={range}
                    onRangeChange={setRange}
                    isZh={isZh}
                />
                <ModelReleaseFrontierSection
                    datasets={dashboard.datasets}
                    range={range}
                    isZh={isZh}
                />
                <ModuleNavigation
                    modules={dashboard.modules}
                    activeId={activeId}
                    onNavigate={setActiveId}
                    isZh={isZh}
                />
                <main className="ai-dev-content">
                    <CapabilitySection datasets={dashboard.datasets} range={range} isZh={isZh} />
                    <CostEfficiencySection datasets={dashboard.datasets} range={range} isZh={isZh} />
                    <UsageMonetizationSection datasets={dashboard.datasets} range={range} isZh={isZh} />
                    <ComputeMarketSection datasets={dashboard.datasets} range={range} isZh={isZh} />
                    <EconomicDiffusionSection datasets={dashboard.datasets} range={range} isZh={isZh} />
                </main>
                <footer className="ai-dev-page-footer">
                    <p>{isZh ? '历史表现不代表未来结果。不同数据源的覆盖范围、样本与定义不同。' : 'Past performance does not predict future results. Coverage, samples and definitions vary by source.'}</p>
                    <span>{dashboard.datasets.filter((dataset) => dataset.status !== 'planned').length} {isZh ? '个已接入数据集' : 'connected datasets'} · {dashboard.sourceCatalog.length} {isZh ? '个来源目录项' : 'cataloged sources'}</span>
                </footer>
            </div>
        </div>
    );
};

export default AIFuturePage;
